// Calendar: two calendars under one switch.
//   • Rolodex, drawn by the app from the Service sheet: every vessel's "Service Start Date" (its Start date,
//     or its Expected date, marked, while it has no Start) and "Service Finish Date", by type and name, in a
//     Month grid (Monday first) or a List of the month day by day. It reads the Service page's own
//     ['service'] query, so it refreshes with it and an entry moves the moment a date changes there, and
//     each vessel gives one entry per kind, so nothing can be doubled (features/serviceCalendar.ts). An
//     entry opens the vessel's service record on the Service page (features/serviceLink.ts).
//   • Google, the person's own Google Calendar, embedded as before (ExternalLinks.tsx).
// People who can read Service open on Rolodex; everyone else sees Google only. Each person's choice is
// remembered on the device, and so is Month or List (with nothing saved, phones open on the List).
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext.js';
import { useSeason } from '../season/SeasonContext.js';
import { useServiceSheet, type OverviewResponse } from '../features/hooks.js';
import {
  addMonths,
  entriesByDay,
  filterEntries,
  monthOf,
  monthWeeks,
  sapKey,
  serviceDateEntries,
  vesselDateColumns,
  type ServiceDateEntry,
} from '../features/serviceCalendar.js';
import { serviceVesselHref } from '../features/serviceLink.js';
import { api } from '../lib/api.js';
import { localTodayIso } from '../lib/dates.js';
import { useDateFormatter } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';
import { Chip, EmptyState, ErrorState, FilterBar, InlineError, LoadingState, PageHelpButton, SearchInput, Segmented } from '../components/ui.js';
import { IconChevronLeft, IconChevronRight } from '../components/icons.js';
import { GoogleCalendar } from './ExternalLinks.js';

type Source = 'rolodex' | 'google';
type View = 'month' | 'list';
const SOURCE_KEY = 'rolodex.calendar.source';
const VIEW_KEY = 'rolodex.calendar.view';

// the source is remembered per person (keyed by their address), so people sharing a PC keep their own
function readSource(who: string): Source | null {
  try {
    const v = window.localStorage.getItem(`${SOURCE_KEY}:${who}`);
    return v === 'rolodex' || v === 'google' ? v : null;
  } catch {
    return null; // private mode / blocked storage: the default
  }
}
function readView(): View | null {
  try {
    const v = window.localStorage.getItem(VIEW_KEY);
    return v === 'month' || v === 'list' ? v : null;
  } catch {
    return null;
  }
}
function remember(key: string, value: string) {
  try { window.localStorage.setItem(key, value); } catch { /* storage blocked: the choice lasts this visit */ }
}

export function Calendar() {
  const { me, can } = useAuth();
  const who = (me?.user.email ?? '').trim().toLowerCase();
  const saved = useMemo(() => readSource(who), [who]);
  const [picked, setPicked] = useState<{ who: string; source: Source } | null>(null);
  // no Service, no service dates: the Google calendar, exactly as it was
  if (!can('service:read')) return <GoogleCalendar />;
  const source: Source = (picked?.who === who ? picked.source : saved) ?? 'rolodex';
  const choose = (s: Source) => {
    setPicked({ who, source: s });
    remember(`${SOURCE_KEY}:${who}`, s);
  };
  const switcher = (
    <Segmented
      value={source}
      onChange={choose}
      ariaLabel="Calendar"
      options={[
        { value: 'rolodex', label: 'Rolodex', title: 'Service start, finish and delivery dates, from the Service sheet' },
        { value: 'google', label: 'Google', title: 'Your own Google Calendar' },
      ]}
    />
  );
  return source === 'google' ? <GoogleCalendar switcher={switcher} /> : <RolodexCalendar switcher={switcher} />;
}

/** a start is a dot (hollow while it's only expected), a finish a square, a delivery a diamond: shape as well
 *  as colour */
const Marker = ({ e }: { e: Pick<ServiceDateEntry, 'kind' | 'expected'> }) => (
  <i className={`cal-mk is-${e.kind}${e.expected ? ' is-expected' : ''}`} aria-hidden="true" />
);
const whatOf = (e: ServiceDateEntry) => (e.expected ? `${e.title} (expected: no Start date yet)` : e.title);
const metaOf = (e: ServiceDateEntry) => [e.group, e.sap && `SAP ${e.sap}`].filter(Boolean).join(' · ');

function RolodexCalendar({ switcher }: { switcher: ReactNode }) {
  const { can } = useAuth();
  const { currentSeason } = useSeason();
  const df = useDateFormatter();
  const isPhone = useIsPhone();
  const { data, isLoading, error, refetch, dataUpdatedAt } = useServiceSheet();
  // the current fleet's models by SAP, as the Service card shows them (the Service page's own query and cache)
  const fleetQ = useQuery({
    queryKey: ['overview', currentSeason],
    queryFn: () => api.get<OverviewResponse>(`/api/overview?season=${encodeURIComponent(currentSeason)}`),
    enabled: can('overview:read') && !!currentSeason,
  });
  const fleetModels = useMemo(() => {
    const m = new Map<string, string>();
    for (const fv of fleetQ.data?.vessels ?? []) {
      const k = sapKey(fv.sap);
      if (k && fv.model.trim()) m.set(k, fv.model.trim());
    }
    return m;
  }, [fleetQ.data]);
  const entries = useMemo(() => (data ? serviceDateEntries(data, fleetModels) : []), [data, fleetModels]);
  const hasDateColumns = useMemo(() => {
    const c = vesselDateColumns(data?.columns ?? []);
    return !!(c.start || c.finish || c.expected || c.delivery);
  }, [data]);

  const today = localTodayIso();
  const [month, setMonth] = useState(() => monthOf(today));
  const [savedView, setSavedView] = useState<View | null>(readView);
  const view: View = savedView ?? (isPhone ? 'list' : 'month');
  const chooseView = (v: View) => {
    setSavedView(v);
    remember(VIEW_KEY, v);
  };
  const [starts, setStarts] = useState(true);
  const [finishes, setFinishes] = useState(true);
  const [deliveries, setDeliveries] = useState(true);
  const [q, setQ] = useState('');
  // phone month: the day tapped (its entries list under the grid); none picked = today, in today's month
  const [picked, setPicked] = useState<string | null>(null);
  // bumped by Today, so the list brings today back into view even when the month didn't change
  const [jump, setJump] = useState(0);

  const weeks = useMemo(() => monthWeeks(month), [month]);
  const monthDays = useMemo(() => weeks.flat().filter((d) => monthOf(d) === month), [weeks, month]);
  const searched = useMemo(() => filterEntries(entries, { starts: true, finishes: true, query: q }), [entries, q]);
  const shown = useMemo(
    () => searched.filter((e) => (e.kind === 'start' ? starts : e.kind === 'finish' ? finishes : deliveries)),
    [searched, starts, finishes, deliveries],
  );
  // the chips count the month in view, under the search
  const counts = useMemo(() => {
    const inMonth = searched.filter((e) => monthOf(e.date) === month);
    const n = (k: ServiceDateEntry['kind']) => inMonth.filter((e) => e.kind === k).length;
    return { start: n('start'), finish: n('finish'), delivery: n('delivery') };
  }, [searched, month]);
  const byDay = useMemo(
    () => new Map(entriesByDay(shown, weeks[0]![0]!, weeks[weeks.length - 1]![6]!).map((d) => [d.date, d.entries])),
    [shown, weeks],
  );
  const listDays = useMemo(() => entriesByDay(shown, monthDays[0]!, monthDays[monthDays.length - 1]!, today), [shown, monthDays, today]);
  const listHasEntries = listDays.some((d) => d.entries.length > 0);

  const go = (by: number) => {
    setMonth((m) => addMonths(m, by));
    setPicked(null);
  };
  const goToday = () => {
    setMonth(monthOf(today));
    setPicked(null);
    setJump((n) => n + 1);
  };

  // a month opens at its top; the current month's list opens at today, and Today goes back there. (On a
  // landscape phone the calendar flows with the page instead of scrolling in its frame, and stays put.)
  const bodyRef = useRef<HTMLDivElement>(null);
  const ready = !!data;
  useLayoutEffect(() => {
    const box = bodyRef.current;
    if (!box || !ready || getComputedStyle(box).overflowY === 'visible') return;
    const at = view === 'list' && month === monthOf(today) ? box.querySelector<HTMLElement>(`[data-day="${today}"]`) : null;
    box.scrollTop = at ? at.offsetTop : 0;
  }, [ready, view, month, jump]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthLabel = df.month(`${month}-01`, { style: 'long' });
  const synced = dataUpdatedAt ? df.time(dataUpdatedAt) : null;
  const activeFilters = (q.trim() ? 1 : 0) + (starts ? 0 : 1) + (finishes ? 0 : 1) + (deliveries ? 0 : 1);
  const clearFilters = () => { setQ(''); setStarts(true); setFinishes(true); setDeliveries(true); };
  const selected = picked ?? (month === monthOf(today) ? today : null);
  const dayTitle = (d: string) => (
    <h3 className="cal-dayh">
      {df.date(d, { weekday: 'long', year: 'auto' })}
      {d === today && <span className="cal-today">Today</span>}
    </h3>
  );

  let body: ReactNode;
  if (isLoading) body = <LoadingState label="Loading service dates…" />;
  else if (error && !data) body = <ErrorState error={error} onRetry={() => void refetch()} />;
  else if (!data) body = null;
  else if (!hasDateColumns) {
    body = <EmptyState title="No service dates yet" hint="The Service sheet has no Start, Finish or Expected date column, so there is nothing to put on the calendar." />;
  } else if (view === 'month') {
    body = (
      <>
        <MonthGrid
          weeks={weeks}
          month={month}
          today={today}
          byDay={byDay}
          compact={isPhone}
          selected={selected}
          onPick={setPicked}
        />
        {isPhone && (
          <section className="cal-dayview" aria-live="polite">
            {selected ? (
              <>
                {dayTitle(selected)}
                {(byDay.get(selected) ?? []).length
                  ? <ul className="cal-rows">{(byDay.get(selected) ?? []).map((e) => <EntryRow key={e.key} e={e} />)}</ul>
                  : <p className="cal-none">No service dates on this day.</p>}
              </>
            ) : <p className="cal-none">Tap a day to see its service dates.</p>}
          </section>
        )}
      </>
    );
  } else if (!listHasEntries) {
    body = (
      <EmptyState
        title={`Nothing in ${monthLabel}`}
        hint={activeFilters ? 'No service dates match the filters this month. Try clearing them.' : 'No vessel starts, finishes or is delivered this month.'}
      />
    );
  } else {
    body = (
      <div className="cal-list">
        {listDays.map(({ date, entries: list }) => (
          <section key={date} className="cal-lday" data-day={date}>
            {dayTitle(date)}
            {list.length
              ? <ul className="cal-rows">{list.map((e) => <EntryRow key={e.key} e={e} />)}</ul>
              : <p className="cal-none">Nothing on today.</p>}
          </section>
        ))}
        <button type="button" className="btn btn-ghost cal-next" onClick={() => go(1)}>
          {df.month(`${addMonths(month, 1)}-01`, { style: 'long' })} <IconChevronRight size={15} />
        </button>
      </div>
    );
  }

  return (
    <div className="embed cal">
      <div className="embed-bar">
        <h1 className="page-title">Calendar</h1>
        {switcher}
        <div style={{ flex: 1 }} />
        {!isPhone && <span className="muted embed-hint">Service dates from the Service sheet{synced ? ` · synced ${synced}` : ''}</span>}
        <PageHelpButton />
      </div>
      <div className="cal-bar">
        <div className="cal-nav">
          <button type="button" className="btn btn-sm cal-step" onClick={() => go(-1)} aria-label="Previous month" title="Previous month"><IconChevronLeft size={16} /></button>
          <h2 className="cal-title" aria-live="polite">{monthLabel}</h2>
          <button type="button" className="btn btn-sm cal-step" onClick={() => go(1)} aria-label="Next month" title="Next month"><IconChevronRight size={16} /></button>
          <button type="button" className="btn btn-sm" onClick={goToday}>Today</button>
        </div>
        <Segmented value={view} onChange={chooseView} ariaLabel="View" options={[{ value: 'month', label: 'Month' }, { value: 'list', label: 'List' }]} />
      </div>
      <FilterBar search={<SearchInput value={q} onChange={setQ} placeholder="Search vessels" width={220} />} activeCount={activeFilters} onClear={clearFilters}>
        <Chip active={starts} onClick={() => setStarts((v) => !v)} title="Service Start Dates, expected ones included">
          <Marker e={{ kind: 'start', expected: false }} />Starts <b className="cal-chipn">{counts.start}</b>
        </Chip>
        <Chip active={finishes} onClick={() => setFinishes((v) => !v)} title="Service Finish Dates">
          <Marker e={{ kind: 'finish', expected: false }} />Finishes <b className="cal-chipn">{counts.finish}</b>
        </Chip>
        <Chip active={deliveries} onClick={() => setDeliveries((v) => !v)} title="Service Delivery Dates: the boat handed back">
          <Marker e={{ kind: 'delivery', expected: false }} />Deliveries <b className="cal-chipn">{counts.delivery}</b>
        </Chip>
        <span className="cal-key"><Marker e={{ kind: 'start', expected: true }} />Expected start: no Start date yet</span>
      </FilterBar>
      {error && data && <InlineError error={error} />}
      <div className="embed-body">
        <div ref={bodyRef} className="cal-body">{body}</div>
      </div>
    </div>
  );
}

/** the month, whole Monday-first weeks. On a desktop each day lists its entries (each one a link to the
 *  vessel); on a phone a day shows a marker per entry and is tapped to list them under the grid. */
function MonthGrid({ weeks, month, today, byDay, compact, selected, onPick }: {
  weeks: string[][];
  month: string;
  today: string;
  byDay: ReadonlyMap<string, ServiceDateEntry[]>;
  compact: boolean;
  selected: string | null;
  onPick: (day: string) => void;
}) {
  const df = useDateFormatter();
  // the full title of a compact entry, under it while the pointer or the keyboard is on it
  const [tip, setTip] = useState<{ e: ServiceDateEntry; x: number; y: number; up: boolean } | null>(null);
  const showTip = (el: HTMLElement, e: ServiceDateEntry) => {
    const r = el.getBoundingClientRect();
    const up = r.bottom + 130 > window.innerHeight;
    setTip({ e, x: Math.max(8, Math.min(r.left, window.innerWidth - 296)), y: up ? r.top - 6 : r.bottom + 6, up });
  };
  // it's pinned to the screen, so any scroll puts it away
  useEffect(() => {
    if (!tip) return;
    const hide = () => setTip(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [tip]);
  return (
    <div className={`cal-month${compact ? ' is-compact' : ''}`} role="table" aria-label={df.month(`${month}-01`, { style: 'long' })}>
      <div className="cal-wk cal-wk-head" role="row">
        {weeks[0]!.map((d) => (
          <div key={d} className="cal-wd" role="columnheader">
            <abbr title={df.weekday(d, 'long')}>{df.weekday(d)}</abbr>
          </div>
        ))}
      </div>
      {weeks.map((week) => (
        <div key={week[0]} className="cal-wk" role="row">
          {week.map((d, i) => {
            const list = byDay.get(d) ?? [];
            const n = Number(d.slice(8));
            // the 1st carries its month, so the days spilling in from either side read right
            const num = n === 1 ? `${n} ${df.month(d, { year: false })}` : String(n);
            const when = `${df.date(d, { weekday: 'long', year: 'auto' })}${d === today ? ', today' : ''}`;
            const cls = `cal-day${monthOf(d) === month ? '' : ' is-out'}${d === today ? ' is-today' : ''}${i >= 5 ? ' is-wknd' : ''}${compact && d === selected ? ' is-picked' : ''}`;
            if (compact) {
              return (
                <div key={d} className={cls} role="cell" aria-current={d === today ? 'date' : undefined}>
                  <button
                    type="button"
                    className="cal-pick"
                    aria-pressed={d === selected}
                    aria-label={`${when}: ${list.length ? `${list.length} service ${list.length === 1 ? 'date' : 'dates'}` : 'nothing on'}`}
                    onClick={() => onPick(d)}
                  >
                    <span className="cal-num">{num}</span>
                    <span className="cal-dots" aria-hidden="true">
                      {list.slice(0, 3).map((e) => <Marker key={e.key} e={e} />)}
                      {list.length > 3 && <span className="cal-more">+{list.length - 3}</span>}
                    </span>
                  </button>
                </div>
              );
            }
            return (
              <div key={d} className={cls} role="cell" aria-current={d === today ? 'date' : undefined}>
                <span className="cal-num"><span aria-hidden="true">{num}</span><span className="sr-only">{when}</span></span>
                {list.length > 0 && (
                  <ul className="cal-evs">
                    {list.map((e) => (
                      <li key={e.key}>
                        <Link
                          className={`cal-ev is-${e.kind}${e.expected ? ' is-expected' : ''}`}
                          to={serviceVesselHref(e.vesselId)}
                          onPointerEnter={(ev) => { if (ev.pointerType !== 'touch') showTip(ev.currentTarget, e); }}
                          onPointerLeave={() => setTip(null)}
                          onFocus={(ev) => showTip(ev.currentTarget, e)}
                          onBlur={() => setTip(null)}
                        >
                          <Marker e={e} />
                          <span className="sr-only">{whatOf(e)}: </span>
                          <span className="cal-ev-t">{e.short}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      ))}
      {tip && createPortal(
        <div className={`cal-tip${tip.up ? ' is-up' : ''}`} style={{ left: tip.x, top: tip.y }} role="tooltip">
          <span className="cal-tip-what"><Marker e={tip.e} />{tip.e.title}{tip.e.expected && <span className="cal-exp">expected</span>}</span>
          <b>{tip.e.label}</b>
          {metaOf(tip.e) && <span>{metaOf(tip.e)}</span>}
          <span>{df.date(tip.e.date, { weekday: 'long', year: 'auto' })}</span>
          {tip.e.expected && <span className="cal-tip-note">Its Expected date: the vessel has no Start date yet.</span>}
          <span className="cal-tip-hint">Click to open its service record</span>
        </div>,
        document.body,
      )}
    </div>
  );
}

/** one entry in the list (and under the phone's month): what it is, the vessel, its group and SAP. The whole
 *  row opens the vessel's service record. */
function EntryRow({ e }: { e: ServiceDateEntry }) {
  const meta = metaOf(e);
  return (
    <li>
      <Link className={`cal-row is-${e.kind}${e.expected ? ' is-expected' : ''}`} to={serviceVesselHref(e.vesselId)}>
        <Marker e={e} />
        <span className="cal-row-what">
          {e.title}
          {e.expected && <span className="cal-exp" title="The vessel has no Start date yet, so its Expected date is shown">expected</span>}
        </span>
        <span className="cal-row-vessel">{e.label}</span>
        <span className="cal-row-meta">{meta}</span>
        <IconChevronRight size={16} className="cal-row-go" />
      </Link>
    </li>
  );
}
